// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import * as cssLinter from "css";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    const { firstName, lastName, userName, userLabel, email } = getSession(state);
    return {
        userFirstName: firstName || lastName || userName || userLabel || email,
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isMyAccountPage: getCurrentPage(state).type === "MyAccountPage",
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasShellContext;

export interface WelcomeMessageStyles {
    container?: InjectableCss;
    welcomeMessageWrapper?: InjectableCss;
    welcomeText?: TypographyProps;
    dashboardLink?: LinkPresentationProps;
}

export const welcomeMessageStyles: WelcomeMessageStyles = {};

const styles = welcomeMessageStyles;

const defaultCustomCss = `
.title-text {
} `;

const WelcomeMessage: React.FC<Props> = ({ id, userFirstName, myAccountPageUrl, isMyAccountPage, fields }) => {
    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.welcomeMessageWrapper}>
                    <Typography {...styles.welcomeText} className="title-text">
                        {translate("Welcome, {0}", userFirstName || "")}
                    </Typography>
                    {myAccountPageUrl && !isMyAccountPage && (
                        <Link
                            href={myAccountPageUrl}
                            {...styles.dashboardLink}
                            aria-label={translate("View My Dashboard")}
                        >
                            {translate("View My Dashboard")}
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(WelcomeMessage)),
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
